import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import api from '../utils/api';
import PreviewRenderer from './PreviewRenderer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PopupPageModal - Displays a page in a modal overlay
 * Used for pages marked as "isPopup" - opens without header/footer
 * Now supports translation: translates popup content when language != default
 */
const PopupPageModal = ({ 
  pageSlug, 
  previewToken,
  isOpen, 
  onClose,
  siteTheme,
  pages = [],
  isPreviewMode = false,
  subdomain = null,
  currentLanguage = null,
  defaultLanguage = 'en',
  tenantId = null
}) => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [translatedBlocks, setTranslatedBlocks] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setPageData(null);
      setTranslatedBlocks(null);
      setLoading(true);
      setError(null);
      return;
    }

    const fetchPage = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let response;
        if (previewToken) {
          response = await api.get(`/public/preview/${previewToken}`);
          setPageData(response.data.page);
        } else if (pageSlug && subdomain) {
          response = await api.get(`/public/tenant/${subdomain}/page/${pageSlug}`);
          setPageData(response.data.page);
        } else if (pageSlug) {
          try {
            response = await api.get(`/pages/by-slug/${pageSlug}`);
            setPageData(response.data);
          } catch (authErr) {
            const foundPage = pages.find(p => p.slug === pageSlug);
            if (foundPage) {
              console.warn('Could not fetch page directly, attempting public fallback');
              setError('Failed to load page - try refreshing');
            } else {
              throw authErr;
            }
          }
        }
      } catch (err) {
        console.error('Failed to load popup page:', err);
        setError('Failed to load page');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [isOpen, pageSlug, previewToken, subdomain, pages]);

  // Translate popup content when language differs from default
  useEffect(() => {
    if (!pageData || !currentLanguage || currentLanguage === defaultLanguage) {
      setTranslatedBlocks(null);
      return;
    }

    const blocks = pageData.blocks || pageData.content || [];
    if (blocks.length === 0) return;

    const translateContent = async () => {
      setIsTranslating(true);
      try {
        const response = await fetch(`${API_URL}/api/translation/translate-blocks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            blocks,
            target_language: currentLanguage,
            tenant_id: tenantId,
            source_language: defaultLanguage
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.translated_blocks) {
            setTranslatedBlocks(data.translated_blocks);
          }
        }
      } catch (err) {
        console.error('[PopupPageModal] Translation error:', err);
      } finally {
        setIsTranslating(false);
      }
    };

    translateContent();
  }, [pageData, currentLanguage, defaultLanguage, tenantId]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const blocksToRender = translatedBlocks || pageData?.blocks || pageData?.content || [];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="popup-page-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-4xl max-h-[90vh] mx-4 bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
          data-testid="popup-close-button"
        >
          <X size={24} className="text-slate-600" />
        </button>
        
        {/* Content */}
        <div className="overflow-y-auto max-h-[90vh] p-6">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center py-20">
              <p className="text-red-500">{error}</p>
            </div>
          )}

          {isTranslating && !loading && (
            <div className="flex items-center gap-2 text-slate-500 mb-4">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
              <span>Translating...</span>
            </div>
          )}
          
          {!loading && !error && pageData && (
            <PreviewRenderer
              blocks={blocksToRender}
              siteTheme={siteTheme}
              pageTheme={pageData.theme}
              pageWidth={pageData.pageWidth || 'standard'}
              pages={pages}
              isPopup={true}
              currentLanguage={currentLanguage}
              defaultLanguage={defaultLanguage}
              tenantId={tenantId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PopupPageModal;
